import React, { useState } from 'react';
import * as api from '../../api/index.js';

import { Button, Form, InputGroup, Badge, Modal } from 'react-bootstrap';
import { deleteVideo } from '../../actions/videos.js';
import { useDispatch } from 'react-redux';
import '../../interfaceSettings.css';
import useAxiosData from './../../hooks/useAxiosData';
import AddVideoDialog from './AddVideoDialog.js';
import AssignBenchesDialog from './AssignBenchesDialog.js';

const VideoControlPanel = () => {
    /** @type {[api.Video[], , React.Dispatch<React.SetStateAction<api.Video[]>>]} */
    const [videos, , setVideos] = useAxiosData(api.fetchVideos, []);
    /** @type {[api.Bench[]]} */
    const [benches, , setBenches] = useAxiosData(api.fetchBenches, []);
    const dispatch = useDispatch();

    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showAssignDialog, setShowAssignDialog] = useState(false);
    const [assigningVideo, setAssigningVideo] = useState();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [videoToDelete, setVideoToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Function to handle delete button click
    const handleDeleteButton = (video) => {
        setVideoToDelete(video);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            dispatch(deleteVideo(videoToDelete._id));
            setVideos(videos.filter(v => v._id !== videoToDelete._id));
        } catch (error) {
            console.error('Error deleting video:', error);
        } finally {
            setShowDeleteModal(false);
            setVideoToDelete(null);
        }
    };

    const getCategoryBadgeColor = (category) => {
        switch (category?.toLowerCase()) {
            case 'health':
                return 'health';  // Green
            case 'exercise':
                return 'exercise';     // Light blue
            case 'proper walking techniques':
                return 'primary';  // Blue
            case 'nutrition':
                return 'warning';  // Yellow
            case 'hydration':
                return 'info';     // Light blue
            case 'injury prevention':
                return 'danger';   // Red
            default:
                return 'secondary'; // Gray
        }
    };

    const handleSearchChange = (event) => setSearchTerm(event.target.value);

    const filteredVideos = videos.filter(({ title, category, subCategory }) =>
        [title, category, subCategory].some(t => t.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    /**
     * @param {api.Video} video 
     */
    function getVideoBenches(video) {
        return benches.filter(b => b.videos.some(({ title }) => title === video.title));
    }

    /**
     * @param {api.Video} video 
     */
    function assignVideoBenches(video) {
        setAssigningVideo(video);
        setShowAssignDialog(true);
    }

    async function updateBenches() {
        setTimeout(async () => {
            setBenches((await api.fetchBenches()).data);
        }, 10);
    }

    return (
        <>
            <AddVideoDialog videos={videos} setVideos={setVideos} showDialog={showAddDialog} setShowDialog={setShowAddDialog} />
            {assigningVideo && <AssignBenchesDialog video={assigningVideo} benches={benches} updateBenches={updateBenches} showDialog={showAssignDialog} setShowDialog={setShowAssignDialog} />}
            <div className='p-4'>
                <h1 id="role-manager-title">Video Manager</h1>
                <div className="container col-lg-12 col-md-10 p-3 pb-3 text-center card shadow-sm">
                    <div className="col-lg-12" style={{ display: 'flex', alignItems: 'center' }}>
                        <InputGroup>
                            <label htmlFor="videoSearch" className="visually-hidden">Search Videos</label>
                            <InputGroup.Text id="bg-body-tertiary justify-content-between basic-addon1">🔎</InputGroup.Text>
                            <Form.Control
                                id="videoSearch"
                                type="search"
                                inputMode="search"
                                className="input-field"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder="Search Videos"
                                aria-label="Search Videos"
                                required
                                style={{ marginRight: '8px' }}
                            />
                        </InputGroup>

                        <Button
                            id='videoAddBtn'
                            onClick={() => setShowAddDialog(true)}
                            className="flex-shrink-0"
                            aria-label="Add new video"
                        >
                            Add
                        </Button>
                    </div>

                    <hr />

                    {/* Desktop/Tablet: Table view */}
                    <div className="table-responsive d-none d-md-block">
                        <table className="table table-hover w-100">
                            <thead>
                                <tr>
                                    <th scope="col">Video</th>
                                    <th scope="col">Video Title</th>
                                    <th scope="col">Category</th>
                                    <th scope="col">Subcategory</th>
                                    <th scope="col">Likes</th>
                                    <th scope="col">Dislikes</th>
                                    <th scope="col">Benches</th>
                                    <th scope="col">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/*Displays Profile in Row */}
                                {
                                    filteredVideos.length > 0 ? (
                                        filteredVideos.map(video => (
                                            <tr key={video._id}>
                                                <td>
                                                    <iframe
                                                        src={video.url} // Use the url property for the iframe
                                                        title={video.title}
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                        loading="lazy"
                                                    ></iframe>
                                                </td>
                                                <td className="align-middle">{video.title}</td>
                                                <td className="align-middle d-md-table-cell">
                                                    <Badge
                                                        className={`me-2 badge-${getCategoryBadgeColor(video.category)}`}
                                                        style={{
                                                            fontSize: '0.9em',
                                                            padding: '8px 12px',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {video.category}
                                                    </Badge>
                                                </td>
                                                <td className="align-middle d-md-table-cell">{video.subCategory}</td>
                                                <td className="align-middle d-md-table-cell">{video.totLikes}</td>
                                                <td className="align-middle d-md-table-cell">{video.totDislikes}</td>
                                                <td className="align-middle">
                                                    {getVideoBenches(video).map(b => `#${b.bench_number}`).join(", ") || "None"}
                                                    <br />
                                                    <a href="#" onClick={() => assignVideoBenches(video)}>(Assign)</a>
                                                </td>
                                                <td className="align-middle">
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="w-100 w-md-auto"
                                                        onClick={() => handleDeleteButton(video)}
                                                    >
                                                        <i className="fas fa-trash-alt me-1"></i>
                                                        Delete
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" style={{ textAlign: 'center', padding: '8px' }}>
                                                No results found
                                            </td>
                                        </tr>
                                    )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile: Card view */}
                    <div className='d-md-none'>
                        {filteredVideos.length > 0 ? (
                            filteredVideos.map(video => (
                                <div key={video._id} className="card mb-3 shadow-sm">
                                    <div className="card-body">
                                        {/* Video Embed */}
                                        <div className="mb-3">
                                            <iframe
                                                src={video.url}
                                                title={video.title}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                loading="lazy"
                                                style={{ width: '100%', aspectRatio: '16/9', border: 'none', borderRadius: '8px' }}
                                            ></iframe>
                                        </div>

                                        {/* Video Title */}
                                        <h2 className="card-title mb-3 h5">{video.title}</h2>

                                        {/* Category & Subcategory */}
                                        <div className="mb-3">
                                            <Badge
                                                className={`badge-${getCategoryBadgeColor(video.category)}`}
                                                style={{
                                                    fontSize: '0.9em',
                                                    padding: '8px 12px',
                                                    marginRight: '8px'
                                                }}
                                            >
                                                {video.category}
                                            </Badge>
                                            <span className="text-muted">{video.subCategory}</span>
                                        </div>

                                        {/* Stats Row */}
                                        <div className="d-flex justify-content-around mb-3 pb-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                                            <div className="text-center">
                                                <div className="text-muted small">Likes</div>
                                                <div className="fw-bold">{video.totLikes}</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-muted small">Dislikes</div>
                                                <div className="fw-bold">{video.totDislikes}</div>
                                            </div>
                                        </div>

                                        {/* Benches */}
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="fw-bold">Benches:</span>
                                                <span className="text-muted">
                                                    {getVideoBenches(video).map(b => `#${b.bench_number}`).join(", ") || "None"}
                                                </span>
                                            </div>
                                            <div className="text-center mt-2">
                                                <a href="#" onClick={() => assignVideoBenches(video)}>
                                                    <i className="fas fa-edit me-1"></i>
                                                    Assign Benches
                                                </a>
                                            </div>
                                        </div>

                                        {/* Delete Button */}
                                        <div className="d-grid">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDeleteButton(video)}
                                            >
                                                <i className="fas fa-trash-alt me-2"></i>
                                                Delete Video
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center p-4 text-muted">
                                No results found
                            </div>
                        )}
                    </div>
                </div>

                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Deletion</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete: <strong>{videoToDelete?.title}</strong>?
                    </Modal.Body>
                    <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                        <Button className="btn-cancel modal-action-btn" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={confirmDelete}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
}

export default VideoControlPanel;
